import styled from '@mui/system/styled'

export const Wrapper = styled('div')(() => ({
  marginTop: '-8px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box'
}))

export const Subtitle = styled('div')(({ theme }) => ({
  color: theme.palette.neutral[300],
  fontSize: 13,
  lineHeight: 1.45,
  '& p': {
    margin: '0 0 8px',
    '&:last-child': { marginBottom: 0 }
  }
}))

export const Deadline = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 14px',
  borderRadius: 8,
  background: '#FFF4E5',
  border: '1px solid #FFD9A8',
  color: '#8A5A00',
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.4
}))

export const CardsRow = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  minWidth: 0
}))

// Card horizontal: coluna da oferta (esquerda) + coluna de benefícios (direita).
export const Card = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  minWidth: 0,
  display: 'grid',
  gridTemplateColumns: 'minmax(280px, 340px) 1px 1fr',
  gap: 24,
  padding: '24px',
  borderRadius: 12,
  border: `1px solid ${theme.palette.primary.main}`,
  background: '#fff',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: 16
  }
}))

// Coluna esquerda: badge, selo, preço, descrição e CTA.
export const CardOffer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  minWidth: 0
}))

// Divisória vertical entre as colunas (some no mobile).
export const CardDivider = styled('div')(({ theme }) => ({
  width: 1,
  alignSelf: 'stretch',
  background: theme.palette.neutral[100],
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}))

// Coluna direita: título + grid de benefícios em 2 colunas.
export const CardBenefits = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  minWidth: 0
}))

interface BadgeProps {
  bgcolor?: string
  fontColor?: string
}

export const Badge = styled('span', {
  shouldForwardProp: (prop) => prop !== 'bgcolor' && prop !== 'fontColor'
})<BadgeProps>(({ theme, bgcolor, fontColor }) => ({
  alignSelf: 'flex-start',
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 12px',
  borderRadius: 6,
  background: bgcolor ?? '#fff',
  color: fontColor ?? theme.palette.neutral[400],
  border:
    bgcolor && bgcolor !== '#fff'
      ? 'none'
      : `1px solid ${theme.palette.neutral[100]}`,
  fontSize: 13,
  fontWeight: 700,
  lineHeight: 1.2
}))

export const PriceRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  color: theme.palette.neutral[400]
}))

export const OldPrice = styled('span')(({ theme }) => ({
  fontSize: 18,
  fontWeight: 600,
  color: theme.palette.neutral[300],
  textDecoration: 'line-through'
}))

export const PriceValue = styled('span')(() => ({
  fontSize: 32,
  fontWeight: 700,
  lineHeight: 1
}))

export const PriceUnit = styled('span')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.neutral[300]
}))

export const NoLoyalty = styled('span')(({ theme }) => ({
  marginTop: -6,
  fontSize: 12,
  fontWeight: 500,
  color: theme.palette.neutral[300]
}))

export const PlanDescription = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: 13,
  lineHeight: 1.5,
  color: theme.palette.neutral[300]
}))

// Destaque do principal gancho da oferta: cobranças grátis todos os meses.
export const FreeBillingsHighlight = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 14px',
  borderRadius: 10,
  background: '#EAF6EA',
  border: `1px solid ${theme.palette.primary.main}`
}))

export const HighlightIcon = styled('span')(({ theme }) => ({
  flexShrink: 0,
  width: 30,
  height: 30,
  borderRadius: '50%',
  background: theme.palette.primary.main,
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 16,
  lineHeight: 1
}))

// Título e subtítulo correm na mesma linha ("50 cobranças grátis todos os meses
// via boleto e PIX") e só quebram quando não couberem.
export const HighlightText = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  columnGap: 0,
  rowGap: 2,
  minWidth: 0
}))

export const HighlightTitle = styled('span')(() => ({
  fontSize: 15,
  fontWeight: 700,
  lineHeight: 1.25,
  color: '#2E7D32',
  '& strong': { fontWeight: 800 }
}))

// Idêntico ao título: os dois formam uma frase única.
export const HighlightSubtitle = styled('span')(() => ({
  fontSize: 15,
  fontWeight: 700,
  lineHeight: 1.25,
  color: '#2E7D32'
}))

// Botão (não link): a contratação abre dentro do CONTA (SC-11070).
export const CtaButton = styled('button')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  padding: '10px 12px',
  borderRadius: 8,
  background: '#111',
  color: '#fff',
  border: '1px solid #111',
  fontSize: 13,
  fontWeight: 600,
  textAlign: 'center',
  textDecoration: 'none',
  cursor: 'pointer',
  lineHeight: 1.3,
  transition: 'background 0.15s ease',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  '&:hover': {
    background: '#000'
  }
}))

// Botão secundário (falar com vendas): outline discreto, não compete com o CTA.
export const SecondaryButton = styled('a')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  width: '100%',
  boxSizing: 'border-box',
  marginTop: -6,
  padding: '10px 12px',
  borderRadius: 8,
  background: '#fff',
  color: theme.palette.neutral[400],
  border: `1px solid ${theme.palette.neutral[100]}`,
  fontSize: 13,
  fontWeight: 600,
  textAlign: 'center',
  textDecoration: 'none',
  cursor: 'pointer',
  lineHeight: 1.3,
  transition: 'border-color 0.15s ease, background 0.15s ease',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  '&:hover': {
    background: '#F4F8F4',
    borderColor: theme.palette.primary.main
  }
}))

// Selos de confiança abaixo dos botões, preenchendo a base da coluna esquerda.
export const TrustList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  padding: '14px 0 0',
  marginTop: 'auto',
  marginBottom: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  borderTop: `1px solid ${theme.palette.neutral[100]}`
}))

export const TrustItem = styled('li')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 12.5,
  fontWeight: 500,
  color: theme.palette.neutral[400]
}))

export const TrustCheck = styled('span')(({ theme }) => ({
  flexShrink: 0,
  color: theme.palette.primary.main,
  fontSize: 13,
  fontWeight: 700,
  lineHeight: 1
}))

export const Divider = styled('div')(({ theme }) => ({
  height: 1,
  background: theme.palette.neutral[100],
  margin: '4px 0'
}))

export const SectionTitle = styled('div')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 700,
  color: theme.palette.neutral[400]
}))

export const BulletList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  columnGap: 20,
  rowGap: 12,
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr'
  }
}))

export const BulletItem = styled('li')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  fontSize: 13,
  color: theme.palette.neutral[300],
  lineHeight: 1.4
}))

export const BulletText = styled('span')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  color: theme.palette.neutral[400],
  fontWeight: 500
}))

export const BulletDetail = styled('span')(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.neutral[300],
  fontWeight: 400,
  lineHeight: 1.4
}))

export const Check = styled('span')(({ theme }) => ({
  flexShrink: 0,
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: theme.palette.primary.main,
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
  marginTop: 1
}))
